/* Entries carry the shape below, so a stale one can be recognised and dropped.
   The version is part of the key: changing it retires every entry written by an
   older format in one step, without needing a migration. */
const CACHE_VERSION = 'v2';
const DEFAULT_TTL_MS = 10 * 60 * 1000;

/* Entries written under a previous version are unreadable now, and localStorage
   keeps them forever otherwise — a browser that has run this app for a while
   holds hundreds. Swept once per page load, not per instance. */
let legacySwept = false;

function sweepSupersededVersions(store) {
    if (legacySwept) return;
    legacySwept = true;
    try {
        Object.keys(store)
            .filter((key) => key.startsWith('dx:') && !key.startsWith(`dx:${CACHE_VERSION}:`))
            .forEach((key) => store.removeItem(key));
    } catch (error) {
        console.debug('Could not sweep superseded cache entries:', error);
    }
}

class CacheService {
    /*
     * Entries expire. Before they did not, and localStorage is forever: a value
     * written against a backend on one port kept being served after that
     * backend moved, with no way to invalidate it short of clearing site data.
     */
    constructor(name, ttlMs = DEFAULT_TTL_MS) {
        this.cache = localStorage;
        this.chains = {};        // Stores the chain of promises per key
        this.releaseFns = {};    // Stores release functions per key
        this.name = name;
        this.ttlMs = ttlMs;
        sweepSupersededVersions(this.cache);
    }

    formatKey(key) {
        return `dx:${CACHE_VERSION}:${this.name}:${key}`;
    }

    createChainLink(key) {
        let releaseFn;
        const newLink = new Promise((resolve) => {
            releaseFn = resolve;
        });
        // If there's no existing chain, start with a resolved promise
        if (!this.chains[key]) {
            this.chains[key] = Promise.resolve();
        }
        // Link this new promise after the existing chain
        const prevLink = this.chains[key];
        this.chains[key] = prevLink.then(() => newLink);
        // Return the release function and the previous link for waiting
        return {releaseFn, prevLink};
    }

    async acquire(key) {
        console.debug(`Acquiring lock for ${key}...`);
        const {releaseFn, prevLink} = this.createChainLink(key);
        // Wait for all previous operations on this key to finish
        await prevLink;

        // We now "hold" the lock until release is called
        this.releaseFns[key] = releaseFn;

        const data = this.getInternally(key);
        console.debug(`Data for ${key}: ${data}`);
        return data;
    }

    async release(key) {
        if (!this.releaseFns[key]) {
            throw new Error(`Key ${key} is not locked.`);
        }
        console.debug(`Releasing lock on ${key}`);
        // Resolve the promise to let the next request proceed
        this.releaseFns[key]();
        delete this.releaseFns[key];
    }

    set(key, data) {
        if (!this.releaseFns[key]) {
            throw new Error(`Trying to set ${key} without acquiring it first.`);
        }
        this.setInternally(key, data);
    }

    setInternally(key, data) {
        console.debug(`Setting ${this.formatKey(key)} in cache`);
        this.cache.setItem(
            this.formatKey(key),
            JSON.stringify({stored_at: Date.now(), data}),
        );
    }

    clear() {
        const prefix = this.formatKey("");
        Object.keys(this.cache)
            .filter((key) => key.startsWith(prefix))
            .forEach((key) => this.cache.removeItem(key));
    }

    getInternally(key) {
        const dataString = this.cache.getItem(this.formatKey(key));
        if (!dataString) return null;

        let entry;
        try {
            entry = JSON.parse(dataString);
        } catch {
            this.cache.removeItem(this.formatKey(key));
            return null;
        }

        const age = Date.now() - (entry?.stored_at ?? 0);
        if (!entry || typeof entry !== "object" || age > this.ttlMs) {
            this.cache.removeItem(this.formatKey(key));
            return null;
        }
        return entry.data ?? null;
    }
}

export {CacheService};
